/* eslint-disable react-hooks/exhaustive-deps */
import { ContractPeriod, ElectricityPackageType } from "components/ElectricityPackages/models";
import React, { Dispatch, SetStateAction } from "react";
import { Form } from "react-bootstrap";
import Select from "react-select";
import { ElectricityPackageUnion, isFixedPackage, UpdateElectricityAgreementRenewalPlanModalForm } from "../modals/UpdateElectricityAgreementRenewalPlanModal";
import { SelectOption } from "common/common-models";

interface UpdateElectricityAgreementRenewalPlanPackageFormProps {
    form: UpdateElectricityAgreementRenewalPlanModalForm;
    setForm: Dispatch<SetStateAction<UpdateElectricityAgreementRenewalPlanModalForm>>;
    packages: ElectricityPackageUnion[];
    contractPeriods: ContractPeriod[];
}

export const getMarginal = (pckg : ElectricityPackageUnion | undefined) => {
    if (!pckg) return;

    let marginal : undefined | number;
    if ("marginal" in pckg) {
        marginal = pckg.marginal;
    }

    if ("marginals" in pckg) {
        marginal = pckg.marginals[0]?.price;
    }

    return marginal;
}

export default function UpdateElectricityAgreementRenewalPlanPackageForm(
    { form, setForm, packages, contractPeriods } : UpdateElectricityAgreementRenewalPlanPackageFormProps
) {
    const consumptionPackages = packages.filter(p => p.type === ElectricityPackageType.CONSUMPTION);
    const productionPackages = packages.filter(p => p.type === ElectricityPackageType.PRODUCTION);

    const contractPeriodOptions : SelectOption<number>[] = contractPeriods.map(c => ({ value: c.id, label: c.name }))
    const packageOptions : SelectOption<number>[] = consumptionPackages
        .sort((a, b) => a.publicId - b.publicId)
        .map(p => ({ value: p.id, label: p.nameEt }))

    const setPackageValue = (option?: any) => {
        const consumptionPackage = consumptionPackages.find(p => p.id === option?.value);
        if(!consumptionPackage) return;

        const consumptionMarginal = getMarginal(consumptionPackage);
        let productionMarginal;
        if (form.hasProduction) {
            const productionPackage = productionPackages.find(p => p.meterType === consumptionPackage?.meterType);
            productionMarginal = getMarginal(productionPackage);
        }
        
        setForm({
            ...form,
            consumptionPackageId: consumptionPackage?.id,
            consumptionMarginal: consumptionMarginal,
            productionMarginal: form.hasProduction ? productionMarginal : undefined,
            basePriceCentsKwh: consumptionPackage?.basePriceCentsKwh,
            monthlyFeeEuros: consumptionPackage?.monthlyFeeEuros,
            nameEt: consumptionPackage?.nameEt,
            fixedPackage: isFixedPackage(consumptionPackage!),
            dynamicPackage: !!consumptionPackage?.dynamicPackage,
            marketPricePackage: !!consumptionPackage?.marketPricePackage,
            twoTariffPackage: !!consumptionPackage?.twoTariffPackage,
            dayTariffCentsKwh: consumptionPackage?.dayTariffCentsKwh,
            nightTariffCentsKwh: consumptionPackage?.nightTariffCentsKwh,
            contractPeriodId: consumptionPackage?.contractPeriod?.id
        })
    }  

    const getCurrentPackageOption = () => {
        if(!form.consumptionPackageId) return;

        const matchingPackage = packageOptions.find(p => p.value === form.consumptionPackageId);
        if(matchingPackage) return matchingPackage;

        return { value: form.consumptionPackageId, label: form.nameEt}
    }

    const currentPackageOption = getCurrentPackageOption();
    const currentContractPeriod = contractPeriodOptions.find(p => p.value === form.contractPeriodId) || null;

    return (
        <div className="row">
            <div className="col-12 col-md-6">
                <Form.Label>Pakett*</Form.Label>
                <Select
                    required
                    options={packageOptions}
                    value={currentPackageOption}
                    onChange={setPackageValue}
                    placeholder='Vali pakett'
                    className={'w-100 form-control form-select' + (!form.consumptionPackageId && form.validated ? ' is-invalid' : '')}
                />
                <Form.Control.Feedback className={'text-right'} type="invalid">Kohustuslik väli</Form.Control.Feedback>
            </div>
            <div className="col-12 col-md-6 mt-3 mt-md-0">
                <Form.Label>Periood*</Form.Label>
                <Select
                    options={contractPeriodOptions}
                    value={currentContractPeriod}
                    onChange={(val: any) => setForm({
                        ...form,
                        contractPeriodId: val.value
                    })}
                    className={'w-100 form-control form-select' + (!form.contractPeriodId && form.validated ? ' is-invalid' : '')}
                    placeholder='Vali lepingu periood'
                    required
                />
                <Form.Control.Feedback className={'text-right'} type="invalid">Kohustuslik väli</Form.Control.Feedback>
            </div>
        </div>
    );
}